import React, {
    useState,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    memo,
    useCallback,
} from "react";
import PropTypes from "prop-types";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { translator } from "@flow-frontend-npm/common";

import EnhancedDataGrid from "./EnhancedDataGrid";
import CustomDataGridToolbar from "./CustomDataGridToolbar/CustomDataGridToolbar";
import usePrevious from "../../hooks/usePrevious";
import useColumns from "./useColumns/useColumns";
import { useSearchTemplateProvider } from "../../provider/SearchTemplateProvider/SearchTemplateProvider";
import { deDE, enUS } from "./customLocales";
import CustomFilterPanel from "flow-core-components-shared/src/utils/CustomFilterPanel";

const { detectLanguage } = translator;

/** Customized DataGrid component for Flow
 * @param {function} clickOnTemplateCreation
 * @param {object} sx
 * @return {JSX.Element}
 */

const FlowDataGrid = memo(
    forwardRef(function FlowDataGrid(
        {
            columns,
            nestedGrid,
            clickOnTemplateCreation,
            gridConfig,
            sx,
            onStateChange,
            rows,
            toolbarProps,
            onFilterModelChange,
            onColumnVisibilityModelChange,
            onPaginationModelChange,
            slots,
            slotProps,
            filterMode,
            ...rest
        },
        ref
    ) {
        const [paginationModel, setPaginationModel] = useState({
            pageSize: 25,
            page: 0,
        });
        const [mounted, setMounted] = useState(false);
        const { columns: parsedColumns } = useColumns(columns);
        const apiRef = useGridApiRef();
        const prevValues = usePrevious({ rows });
        const {
            setSaveTemplateDialogOpen,
            nestedDataGridConfig,
            dataGridConfig,
            setDataGridConfig,
            setNestedDataGridConfig,
            setDataGridConfigIsDirty,
            subscribeEvent,
        } = useSearchTemplateProvider() || {
            setSaveTemplateDialogOpen: null,
            dataGridConfig: null,
            nestedDataGridConfig: null,
            setDataGridConfig: null,
            setNestedDataGridConfig: null,
            setDataGridConfigIsDirty: null,
            subscribeEvent: null,
        };
        const initialState = useRef();

        const localeTexts = detectLanguage() === "de" ? deDE : enUS;

        useEffect(() => {
            if (gridConfig) {
                loadGridConfig(gridConfig);
            }
        }, [gridConfig]);

        useEffect(() => {
            if (!gridConfig && nestedGrid && mounted && nestedDataGridConfig) {
                loadGridConfig(nestedDataGridConfig);
            }
        }, [nestedDataGridConfig]);

        useEffect(() => {
            if (apiRef && onStateChange) {
                return apiRef.current.subscribeEvent(
                    "stateChange",
                    onStateChange
                );
            }
        }, [apiRef, onStateChange]);

        // eslint-disable-next-line require-jsdoc
        const createDataGridConfig = useCallback(
            ({ columnOrder, columnWidth } = {}) => {
                const gridState = apiRef.current.exportState();
                return {
                    checkboxSelection: !!rest.checkboxSelection,
                    columns: apiRef.current
                        .getAllColumns()
                        .map(({ field, width }) => ({
                            field,
                            width: columnWidth?.[field] ? columnWidth[field] : width,
                            hide:
                                gridState.columns?.columnVisibilityModel?.[
                                    field
                                ] === false,
                            orderIndex:
                                columnOrder?.[field] ||
                                apiRef.current.getColumnIndex(field, false),
                        })),
                    filterModel: gridState.filter.filterModel,
                    sortModel: gridState.sorting.sortModel,
                };
            },
            [apiRef, rest.checkboxSelection],
        );

        useImperativeHandle(
            ref,
            () => ({
                setColumnVisibilityModel:
                    apiRef.current.setColumnVisibilityModel,
                exportState: apiRef.current.exportState,
                forceUpdate: apiRef.current.forceUpdate,
                setRows: apiRef.current.setRows,
                getSelectedRows: apiRef.current.getSelectedRows,
                // eslint-disable-next-line require-jsdoc
                addRows(rows, sort) {
                    apiRef.current.updateRows(rows);
                    if (sort) apiRef.current.applySorting();
                },
                // eslint-disable-next-line require-jsdoc
                getApi() {
                    return apiRef.current;
                },
            }),
            [apiRef, createDataGridConfig]
        );

        /**
         * Load configuration of the result grid
         * @param {object} gridConfig
         */
        const loadGridConfig = useCallback(
            ({ columns: columnSettings, filterModel, sortModel }) => {
                if (Array.isArray(columnSettings)) {
                    apiRef.current.setColumnVisibilityModel(
                        columns.reduce(
                            (prev, { field }) => ({ ...prev, [field]: false }),
                            {}
                        )
                    );
                    columnSettings.forEach(
                        ({ field, width, hide, orderIndex }) => {
                            apiRef.current.setColumnVisibility(field, !hide);
                            apiRef.current.setColumnWidth(field, width);
                            apiRef.current.setColumnIndex(field, orderIndex);
                        }
                    );
                }

                if (filterModel) {
                    apiRef.current.setFilterModel(filterModel);
                }

                if (sortModel) {
                    apiRef.current.setSortModel(sortModel);
                }
            },
            [apiRef]
        );

        /**
         * Called when the grid config updated
         */
        const handleConfigChange = ({ eventName, details, field, width }) => {
            if (
                typeof setDataGridConfigIsDirty === "function" &&
                mounted &&
                !(eventName === "onFilterModelChange" && !details.reason)
            ) {
                setDataGridConfigIsDirty(true);
            }

            if (typeof setDataGridConfig === "function" && !nestedGrid) {
                setDataGridConfig(createDataGridConfig());
            }

            if (
                typeof setNestedDataGridConfig === "function" &&
                nestedGrid &&
                eventName !== "onColumnVisibilityModelChange" &&
                !nestedDataGridConfig
            ) {
                setNestedDataGridConfig(createDataGridConfig());
            }

            if (
                typeof setNestedDataGridConfig === "function" &&
                nestedGrid &&
                eventName === "onColumnResize"
            ) {
                setNestedDataGridConfig(createDataGridConfig({ columnWidth: { [field]: width }}));
            }
        };

        /**
         * Called when the "SAVE SEARCH" button pressed
         */
        const onClickTemplateCreationButton = () => {
            if (typeof clickOnTemplateCreation === "function") {
                clickOnTemplateCreation();
            }

            if (typeof setSaveTemplateDialogOpen === "function") {
                setSaveTemplateDialogOpen(true);
            }
        };

        useEffect(() => setMounted(true), []);

        useEffect(() => {
            if (
                typeof setDataGridConfig === "function" &&
                mounted &&
                !nestedGrid
            ) {
                const columnOrder = dataGridConfig?.columns?.reduce(
                    (prevValue, { field, orderIndex }) => ({
                        ...prevValue,
                        [field]: orderIndex,
                    }),
                    {}
                );
                setDataGridConfig(createDataGridConfig({ columnOrder }));
            }
        }, [createDataGridConfig, mounted, setDataGridConfig]);

        /**
         * loadGridConfig can not run, when the rows value lenght is 0
         * Run it again only after first update with checking previous value
         */
        useEffect(() => {
            if (gridConfig && prevValues?.rows && !nestedGrid) {
                loadGridConfig(gridConfig);
            }
        }, [rows]);

        useEffect(() => {
            if (
                nestedGrid &&
                mounted &&
                Array.isArray(rows) &&
                nestedDataGridConfig
            ) {
                loadGridConfig(nestedDataGridConfig);
            }
        }, [mounted, rows]);

        useEffect(() => {
            if (typeof subscribeEvent === "function") {
                initialState.current = apiRef.current.exportState();

                const resetEvent = subscribeEvent("onReset", () => {
                    apiRef.current.restoreState(initialState.current);
                });

                const onTemplateSelectionEvent = subscribeEvent(
                    "onTemplateSelection",
                    (templateContent) => {
                        loadGridConfig(templateContent.resultTableConfig);
                    }
                );

                return () => {
                    resetEvent.unsubscribe();
                    onTemplateSelectionEvent.unsubscribe();
                };
            }
        }, []);

        useEffect(() => {
            if (mounted && typeof onPaginationModelChange === "function") {
                onPaginationModelChange(paginationModel, {
                    api: apiRef.current,
                });
            }
        }, [paginationModel]);

        /**
         * After re-rendering the dataGrid component the column order and width set back to initial state
         * (copy from template data)
         */
        useEffect(() => {
            if (dataGridConfig && mounted && !nestedGrid) {
                dataGridConfig.columns.forEach(
                    ({ field, orderIndex, width }) => {
                        apiRef.current.setColumnWidth(field, width);
                        apiRef.current.setColumnIndex(field, orderIndex);
                    },
                );
            }

            if (nestedDataGridConfig && mounted && nestedGrid) {
                nestedDataGridConfig.columns.forEach(
                    ({field, width}) => {
                        apiRef.current.setColumnWidth(field, width);
                    }
                );
            }
        });

        const handleFilterModelChange = (model, details, ...rest) => {
            if (typeof onFilterModelChange === "function") {
                if (filterMode !== "server") {
                    onFilterModelChange(model, details, ...rest);
                    return;
                }

                const columns = details.api.getAllColumns();

                const getColumnFilterOperatorValueRequirement = (
                    field,
                    operator
                ) => {
                    const requiresFilterValue = columns
                        .find(({ field: fieldName }) => field === fieldName)
                        .filterOperators.find(
                            ({ value }) => value === operator
                        ).requiresFilterValue;

                    return requiresFilterValue === false ? false : true;
                };

                const modelMutation = {
                    ...model,
                    items: model.items.filter(({ field, value, operator }) => {
                        const filterValueRequired =
                            getColumnFilterOperatorValueRequirement(
                                field,
                                operator
                            );

                        return (
                            filterValueRequired === false ||
                            (filterValueRequired && (value || value === false))
                        );
                    }),
                };

                onFilterModelChange(modelMutation, details, ...rest);
            }
        };

        return (
            <EnhancedDataGrid
                apiRef={apiRef}
                filterMode={filterMode || "client"}
                columns={parsedColumns}
                autoHeight
                striped
                disableRowSelectionOnClick
                rowHeight={42}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[25, 50, 100]}
                slots={{
                    toolbar: CustomDataGridToolbar,
                    filterPanel: CustomFilterPanel,
                    ...slots,
                }}
                slotProps={{
                    toolbar: {
                        clickOnTemplateCreation:
                            clickOnTemplateCreation || setSaveTemplateDialogOpen
                                ? onClickTemplateCreationButton
                                : null,
                        ...(toolbarProps && typeof toolbarProps === "object"
                            ? toolbarProps
                            : {}),
                    },
                    filterPanel: {
                        sx: {
                            "& .MuiDataGrid-filterForm": {
                                gap: 2,
                            },
                        },
                    },
                    baseTooltip: {
                        arrow: true,
                    },
                    columnsPanel: {
                        sx: {
                            "& .MuiDataGrid-columnsPanelRow": {
                                mt: 1,
                            },
                        },
                    },
                    ...slotProps,
                }}
                localeText={localeTexts}
                sx={{
                    'input[type="checkbox"]': {
                        height: "42px !important",
                        width: "42px !important",
                    },
                    fontSize: "16px",
                    border: 0,
                    ...sx,
                }}
                onFilterModelChange={(model, details, ...rest) => {
                    handleConfigChange({
                        eventName: "onFilterModelChange",
                        details,
                    });
                    handleFilterModelChange(model, details, ...rest);
                }}
                onColumnOrderChange={() => {
                    handleConfigChange({ eventName: "onColumnOrderChange" });
                }}
                onColumnVisibilityModelChange={(model, details) => {
                    if (typeof onColumnVisibilityModelChange === "function") {
                        onColumnVisibilityModelChange(model, details);
                    }
                    handleConfigChange({
                        eventName: "onColumnVisibilityModelChange",
                    });
                }}
                onCellModesModelChange={() => {
                    handleConfigChange({
                        eventName: "onCellModesModelChange",
                    });
                }}
                onColumnResize={({width, colDef: {field} }) => {
                    handleConfigChange({ eventName: "onColumnResize", field, width });
                }}
                onSortModelChange={() => {
                    handleConfigChange({
                        eventName: "onSortModelChange",
                    });
                }}
                rows={rows}
                {...rest}
            />
        );
    })
);

FlowDataGrid.displayName = "FlowDataGrid";
FlowDataGrid.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object),
    nestedGrid: PropTypes.bool,
    clickOnTemplateCreation: PropTypes.func,
    gridConfig: PropTypes.shape({
        columns: PropTypes.array,
        filterModel: PropTypes.object,
    }),
    sx: PropTypes.object,
    onStateChange: PropTypes.func,
    rows: PropTypes.arrayOf(PropTypes.object),
    toolbarProps: PropTypes.object,
    onFilterModelChange: PropTypes.func,
    onColumnVisibilityModelChange: PropTypes.func,
    onPaginationModelChange: PropTypes.func,
    slots: PropTypes.object,
    slotProps: PropTypes.object,
    filterMode: PropTypes.string,
};

export default FlowDataGrid;
